import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component, ElementRef,
  EventEmitter, OnDestroy,
  OnInit,
  Output, TemplateRef,
  ViewChild
} from '@angular/core';
import {FormControl, FormGroup, Validators} from '@angular/forms';
import {LoginInternalService} from '../../../services/login-internal.service';
import {ActivatedRoute, Router} from '@angular/router';
import {PreLoginSystemConfigurationValues} from '../../../../common/configuration/services/prelogin.system.configuration.manager';
import {ChangePasswordComponent} from '../../../../common/change.password/components/change.password.component';
import {takeUntilDestroy} from 'take-until-destroy';
import {PopupWindowService} from '../../../../common/services/popup.window.service';
import {ContactPageComponent} from "../../../../common/components/contact.us.page/contact-page.component";
import {AboutPageComponent} from "../../../../common/components/about.page/about.page.component";
import {IVersionDetails} from "../../../../common/versiont.details/interfaces/version.details";
import {filter} from "rxjs/operators";
import {VersionDetailsService} from "../../../../common/versiont.details/services/version.details.service";
import {LogManager} from "../../../../common/log/log.manager";
import {LogCat} from "../../../../common/log/log.categories";
import {TermsPageComponent} from "../../../../common/components/terms.page/terms.page.component";

@Component({
  selector: 'app-login',
  templateUrl: './login.component.html',
  styleUrls: ['./login.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class LoginComponent implements OnInit, OnDestroy {
  @ViewChild('changePasswordTitle', { static: true }) changePasswordTitle: TemplateRef<any>;
  @ViewChild('contactUsTitle', { static: true }) contactUsTitle: TemplateRef<any>;
  @ViewChild('aboutTitle', { static: true }) aboutTitle: TemplateRef<any>;
  @ViewChild('termsTitle', { static: true }) termsTitle: TemplateRef<any>;
  @ViewChild("Password", {static: false}) public passwordCrl: ElementRef;
  @Output() createAccount = new EventEmitter();
  @Output() forgotPassword = new EventEmitter();

  public formControls: FormGroup;
  public loginInProgress = false;
  private returnUrl: string;

  public allowCreateAccount = false;
  public isUserNameFilled = '';
  public isPasswordFilled = '';
  public passwordVisible = false;
  public contactInfo: string;

  public errorText1 = '';
  public errorText1Args = [];
  public errorText2 = '';
  public errorText3 = '';
  public errorActionTextIndex = 0;
  public errorAction: any;
  public contactInfoData = '';

  public userName: string;
  public password: string;

  constructor(private authService: LoginInternalService,
              private route: ActivatedRoute,
              private router: Router,
              private changeDetector: ChangeDetectorRef,
              private sysConfig: PreLoginSystemConfigurationValues,
              private versionDetailsService: VersionDetailsService,
              public popupWindowService: PopupWindowService) {
    this.sysConfig.GetMainContactTelephoneAsync().subscribe(value => {
      this.contactInfo = value;
      this.changeDetector.markForCheck();
    });

    this.sysConfig.GetCreateAccountEnabledAsync().subscribe(value => {
      this.allowCreateAccount = value;
      this.changeDetector.markForCheck();
    });

  }

  ngOnInit() {
    this.returnUrl = this.route.snapshot.queryParams.returnUrl || '/';

    // always take the first level of routing
    if (this.returnUrl !== '/') {
      this.returnUrl = '/' + this.returnUrl.split('/')[1];
    }

    this.formControls = new FormGroup({
      userName: new FormControl('', {validators: [Validators.required]}),
      password: new FormControl('', {validators: [Validators.required]})
    });

    // Initialize version details data
    this.versionDetailsService.load().pipe(filter(x => x != null),
          takeUntilDestroy(this)).subscribe(() => {
            LogManager.debug(LogCat.Login.VersionDetails,  "Version details loaded successfully");
        }, (err) => {
          LogManager.error(LogCat.Login.VersionDetails,  "Failed to load version details");
    });
  }

  ngOnDestroy(): void { }

  public newAccount() {
    this.createAccount.emit();
  }

  public doForgotPassword() {
    this.forgotPassword.emit();
  }

  public login() {
    if (this.isValid()) {
      this.doLogin(this.userName, this.password);
    }
  }

  private isValid(): boolean {
    this.isUserNameFilled = 'valid';
    this.isPasswordFilled = 'valid';
    if (this.formControls.controls.userName.invalid) {
      this.isUserNameFilled = 'invalid';
    }
    if (this.formControls.controls.password.invalid) {
      this.isPasswordFilled = 'invalid';
    }
    if (this.isUserNameFilled === 'invalid' || this.isPasswordFilled === 'invalid') {
      return false;
    }
    return true;
  }

  private doLogin(userName: string, password: string) {
    this.loginInProgress = true;
    this.resetError();
    this.changeDetector.markForCheck();
    this.authService.login(userName, password).then((data) => {
      this.router.navigate([this.returnUrl]);
    }, error => {
      this.loginInProgress = false;
      this.setError(error);
      this.changeDetector.markForCheck();
    });
  }

  public reset() {
    this.isUserNameFilled = '';
    this.isPasswordFilled = '';
    this.formControls.reset();
    this.loginInProgress = false;
    this.resetError();
    this.changeDetector.markForCheck();
  }

  public togglePasswordVisibility() {
    this.passwordVisible = !this.passwordVisible;
  }

  public setError(error: string) {
    if (error === 'PatientPortalPasswordExpired') {
      this.changePassword();
    } else if (error === 'PatientPortalUnAuthorized') {
      this.errorText1 = error;
      if (this.allowCreateAccount) {
        this.errorText2 = 'PleaseTryAgainOr';
        this.errorText3 = 'CreateAccountLowerCase';
        this.errorActionTextIndex = 3;
        this.errorAction = this.newAccount;
      }
    } else if (error === 'PatientPortalAccountLocked') {
      this.errorText1 = error;
      this.errorText1Args = [this.sysConfig.LockExpirationTimeInMinutes.toString()];
    } else {
      this.errorText1 = error;
      if (this.contactInfo) {
        this.errorText2 = 'PleaseContactUs';
        this.contactInfoData = this.contactInfo;
      }
    }
  }

  public changePassword() {
    this.popupWindowService
      .openPopupWindow(ChangePasswordComponent, this.changePasswordTitle, window.outerWidth - 20, window.outerHeight - 20, 10, 10,
        {userName: this.formControls.controls.userName.value})
      .pipe(takeUntilDestroy(this))
      .subscribe((result) => {
          this.password = '';
          this.passwordCrl.nativeElement.focus();
          this.changeDetector.markForCheck();
      });
  }

  public showContactUsPage() {
    this.popupWindowService
      .openPopupWindow(ContactPageComponent, this.contactUsTitle, window.outerWidth - 20, window.outerHeight - 20, 10, 10)
      .pipe(takeUntilDestroy(this))
      .subscribe((result) => {
        if (result === true) {
        }
      });
  }

  public showAboutPage() {
    this.popupWindowService
      .openPopupWindow(AboutPageComponent, this.aboutTitle, window.outerWidth - 20, window.outerHeight - 20, 10, 10)
      .pipe(takeUntilDestroy(this))
      .subscribe();
  }

  public showTermsPage() {
    this.popupWindowService
      .openPopupWindow(TermsPageComponent, this.termsTitle, window.outerWidth - 20, window.outerHeight - 20, 10, 10)
      .pipe(takeUntilDestroy(this))
      .subscribe();
  }

  public resetError() {
    this.errorText1 = '';
    this.errorText2 = '';
    this.errorText3 = '';
    this.errorActionTextIndex = 0;
    this.errorAction = null;
    this.contactInfoData = '';
  }

  public onKeyDown($event: KeyboardEvent) {
    if ($event.key === 'Enter') {
      this.login();
    }
  }
}
